import { Injectable, ElementRef } from '@angular/core';
import { LZString } from '../core/util/lzstring';
import { STUDENT_ACTIVITY_SLUGS } from './test-runner/test-runner.component.js';

export enum MediaEvent {
  INIT_COMPONENT = 'INIT_COMPONENT',
  DESTROY_COMPONENT = 'DESTROY_COMPONENT',
  URL_CHANGED = 'URL_CHANGED',
  PLAY = 'PLAY',
  PAUSE = 'PAUSE',
  ENDED = 'ENDED',
}

@Injectable({
  providedIn: 'root'
})
export class InteractionTrackingService {
  private inputDeleteListeners = new Map<Element, EventListener>();
  private trackedElements = new Map<ElementRef, {
    inputChangeHandlerRef?: EventListenerOrEventListenerObject,
    firstInteractionHandlerRef?: EventListenerOrEventListenerObject,
    firstInteractionInputHandlerRef?: EventListenerOrEventListenerObject
  }>();
  private logFunction: ((slug: any, info: any, slugPrefix?: string) => void) | null = null;
  private inputCallback: ((currentText?: string) => void) | null = null;
  private mcqCallback: ((questionId: number, optionData: any) => void) | null = null;

  constructor() { }

  setLogFunction(logFn: (slug: any, info: any, slugPrefix?: string) => void) {
    this.logFunction = logFn;
  }

  setInputCallback(callback: (currentText?: string) => void) {
    this.inputCallback = callback;
  }

  setMcqCallback(callback: (questionId: number, optionData: any) => void) {
    this.mcqCallback = callback;
  }

  public logEvent(slug: any, info: any, slugPrefix?: string) {
    if (this.logFunction) {
      this.logFunction(slug, { ...info }, slugPrefix);
    }
    
    // Handle MCQ responses for responseFirst/responseLast timestamps
    if (slug === 'MCQ_ELEMENT_RESPONSE' && this.mcqCallback) {
      this.mcqCallback(0, info); // questionId will be determined by test-runner via getActiveQuestionId()
    }
  }

  trackInputChanges(elementRef: ElementRef, callback?: (event: Event) => void) {
    const element = elementRef.nativeElement;
    if (!element) return;

    // Clean up any previous input change listeners for this element
    this.cleanupInputChangesListeners(elementRef);

    // Create the input change handler
    const inputChangeHandler: EventListenerOrEventListenerObject = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target && element.contains(target)) {
        // Register a delete key listener if not already registered
        if (!this.inputDeleteListeners.has(target)) {
          const deleteListener = (e: KeyboardEvent) => {
            if (e.key === 'Backspace' || e.key === 'Delete') {
              setTimeout(() => {
                if (callback) callback(e); // Send full event after deletion
                this.logInteraction('TEXT_INPUT_ELEMENT_RESPONSE', { value: target.innerText || target.value, eventType: 'delete' }, elementRef);
                // Call input callback for delete operations
                if (this.inputCallback) {
                  this.inputCallback(target.innerText || target.value);
                }
              }, 50);
            }
          };
          target.addEventListener('keydown', deleteListener, true);
          this.inputDeleteListeners.set(target, deleteListener);
        }

        if (callback) callback(event); // Regular input event callback
        this.logInteraction('TEXT_INPUT_ELEMENT_RESPONSE', { value: target.innerText || target.value, eventType: 'input' }, elementRef);
        
        // Call input callback - the test-runner will determine if this is first input
        if (this.inputCallback) {
          this.inputCallback(target.innerText || target.value);
        }
      }
    };

    // Add the input event listener on the specific element
    element.addEventListener('input', inputChangeHandler, true);

    // Store the handler reference associated with the elementRef
    const trackedInfo = this.trackedElements.get(elementRef) || {};
    trackedInfo.inputChangeHandlerRef = inputChangeHandler;
    this.trackedElements.set(elementRef, trackedInfo);
  }

  /**
   * Manually trigger input change tracking for programmatically updated input values
   * This is used when input values are changed via setValue() or other programmatic means
   * that don't trigger the native 'input' event of the input element.
   */
  trackProgrammaticInputChange(elementRef: ElementRef, inputValue?: string) {
    this.logInteraction('TEXT_INPUT_ELEMENT_RESPONSE', { 
      value: inputValue, 
      eventType: 'input' 
    }, elementRef);
    
    if (this.inputCallback) {
      this.inputCallback(inputValue);
    }
  }

  trackFirstInteraction(elementRef: ElementRef) {
    const element = elementRef.nativeElement;
    if (!element) return;

    // Clean up any previous first interaction listeners for this element
    this.cleanupFirstInteractionListeners(elementRef);

    let hadFirstInteractionForElement = false;

    const handler = (event: Event) => {
      if(hadFirstInteractionForElement) return;
      const target = event.target as Element;
      if (!element.contains(target)) return;

      const isInteractionClick = 
        event.type === 'click' &&
        (
          target.closest('button') ||
          target.closest('element-render-interactive-diagram') ||
          target.closest('element-render-custom-interaction') ||
          target.closest('element-render-numberline') ||
          target.closest('element-render-select-text')
        );

      if (isInteractionClick) {
        hadFirstInteractionForElement = true;
        this.logInteraction('FIRST_INTERACTION', { type: 'click', event: LZString.compressToBase64(JSON.stringify(event)) }, elementRef);
        this.cleanupFirstInteractionListeners(elementRef);
      }
    };

    const inputHandler = (event: Event) => {
      if (hadFirstInteractionForElement) return;
      const target = event.target as Element;
      if (!element.contains(target)) return;

      hadFirstInteractionForElement = true;
      this.logInteraction('FIRST_INTERACTION', { type: 'input', event: LZString.compressToBase64(JSON.stringify(event)) }, elementRef);
      this.cleanupFirstInteractionListeners(elementRef);
    };

    element.addEventListener('click', handler, true);
    element.addEventListener('input', inputHandler, true);

    // Store the handler references associated with the elementRef
    const trackedInfo = this.trackedElements.get(elementRef) || {};
    trackedInfo.firstInteractionHandlerRef = handler;
    trackedInfo.firstInteractionInputHandlerRef = inputHandler;
    this.trackedElements.set(elementRef, trackedInfo);
  }

  cleanupFirstInteractionListeners(elementRef: ElementRef) {
    const element = elementRef.nativeElement;
    const trackedInfo = this.trackedElements.get(elementRef);

    if (element && trackedInfo) {
      if (trackedInfo.firstInteractionHandlerRef) {
        element.removeEventListener('click', trackedInfo.firstInteractionHandlerRef, true);
        trackedInfo.firstInteractionHandlerRef = undefined;
      }
      if (trackedInfo.firstInteractionInputHandlerRef) {
        element.removeEventListener('input', trackedInfo.firstInteractionInputHandlerRef, true);
        trackedInfo.firstInteractionInputHandlerRef = undefined;
      }
      // Clean up the entry in the map if no other listeners remain for this element
      if (!trackedInfo.inputChangeHandlerRef) {
        this.trackedElements.delete(elementRef);
      } else {
        this.trackedElements.set(elementRef, trackedInfo);
      }
    }
  }

  cleanupInputChangesListeners(elementRef: ElementRef) {
    const element = elementRef.nativeElement;
    const trackedInfo = this.trackedElements.get(elementRef);

    if (element && trackedInfo) {
      if (trackedInfo.inputChangeHandlerRef) {
        element.removeEventListener('input', trackedInfo.inputChangeHandlerRef, true);
        trackedInfo.inputChangeHandlerRef = undefined;
      }

      // Clean up associated delete key listeners within this element's scope
      this.inputDeleteListeners.forEach((listener, targetElement) => {
        if (element.contains(targetElement)) {
          targetElement.removeEventListener('keydown', listener, true);
          this.inputDeleteListeners.delete(targetElement);
        }
      });

      // Clean up the entry in the map if no other listeners remain for this element
      if (!trackedInfo.firstInteractionHandlerRef && !trackedInfo.firstInteractionInputHandlerRef) {
        this.trackedElements.delete(elementRef);
      } else {
        this.trackedElements.set(elementRef, trackedInfo);
      }
    }
  }

  private logInteraction(slug: string, info: any, elementRef?: ElementRef) {
    if (this.logFunction) {
      this.logFunction(slug, { ...info }, 'STUDENT_TEST_ITEM');
    }
  }

  cleanup() {
    // Iterate over a copy of the keys to avoid issues while modifying the map
    const elementRefs = Array.from(this.trackedElements.keys());
    elementRefs.forEach(elementRef => {
      this.cleanupFirstInteractionListeners(elementRef);
      this.cleanupInputChangesListeners(elementRef);
    });
    this.setLogFunction(null);
    // Should be empty already, but clear just in case
    this.trackedElements.clear();
    this.inputDeleteListeners.clear();
  }
}